import React from "react";
import PropTypes from "prop-types";
import { moneyToString, stringToMoney } from "lib/money";

/**
 * MoneyInput - allow the user to input an amount of money
 * Uses <input type="money" /> for two reasons:
 * - On mobile, the number keyboard will be shown instead of text keyboard
 * - The browser restricts the input to numbers, preventing letters
 * If the input loses focus when it is empty, the `defaulAmount` will
 *   be inserted.
 *
 * @param {number} defaultAmount fallback value to use when the input is left empty
 * @param {Function} [validateInput] called on every inpu to decide whether it is valid
 * @param {Function} onChange will only ever be called with valid money amounts
 */
export default class MoneyInput extends React.Component {

  static propTypes = {
    initialAmount: PropTypes.number.isRequired,
    defaultAmount: PropTypes.number.isRequired,
    validateInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    readOnly: PropTypes.bool,
    onReadOnlyClick: PropTypes.func
  }

  static defaultProps = {
    validateInput: () => true,
    readOnly: false
  }

  /**
   * Initialise input value to `defaultAmount` prop.
   */
  static getDerivedStateFromProps(nextProps, prevState) {
    if (prevState.inputValue === undefined) {
      return {
        inputValue: moneyToString(nextProps.initialAmount)
      }
    }
    return null;
  }

  state = {
    inputValue: undefined,
    isValid: true
  }

  setValidity(isValid) {
    this.setState({ isValid });
    if (this.props.onValidityChange) {
      this.props.onValidityChange(isValid);
    }
  }

  /**
   * When input changes, update state.
   * Also, iff input is valid money, call onChange.
   */
  onChange = () => {

    // track raw value in state always
    const inputValue = this.input.value;
    this.setState({ inputValue });

    // only update global state if input is valid money
    let inputMoney;
    try {
      inputMoney = stringToMoney(inputValue);
    } catch (error) {
      this.setValidity(false);
      return;
    }

    if (this.props.validateInput(inputMoney)) {
      this.setValidity(true);
      this.props.onChange(inputMoney);
    } else {
      this.setValidity(false);
    }

  }

  /**
   * Fire a callback if the input is clicked while it is read-only
   */
  onClick = () => {
    if (this.props.readOnly && this.props.onReadOnlyClick !== undefined) {
      this.props.onReadOnlyClick();
    }
  }

  /**
   * When focus leaves the input, that is our opportunity to mangle it.
   * If the user left the input balnk, then we re-fill with the `defaultAmount`.
   */
  onBlur = () => {
    // replace blank input with default amount
    if (this.state.inputValue.trim().length === 0) {
      this.setState({
        inputValue: moneyToString(this.props.defaultAmount)
      });
      this.props.onChange(this.props.defaultAmount);
      this.setValidity(true);
    }
    // normalise input
    if (this.state.isValid) {
      this.setState({
        inputValue: moneyToString(stringToMoney(this.state.inputValue))
      })
    }
  }

  refInput = input => {
    this.input = input;
  }

  render() {
    const { className, readOnly } = this.props;

    let classNames = [ "money-input" ];
    if (className) {
      classNames.push(className);
    }

    return (
      <div className={classNames.join(" ")}>
        <span className="money-input__label">£</span>
        <input
          className="money-input__field"
          type="number"
          ref={this.refInput}
          value={this.state.inputValue}
          onChange={this.onChange}
          onBlur={this.onBlur}
          readOnly={readOnly}
          onClick={this.onClick}
        />
      </div>
    )
  }

}



// WEBPACK FOOTER //
// ./src/components/MoneyInput.js